import React, { useContext } from "react"
import { CheckCircle } from "../../assets/icons"
import { AddComment, AddCommentOutlined, Cancel, DeleteOutline, MoreVert, Notes, Visibility, VisibilityOff } from "@mui/icons-material"
import moment from "moment"
import { dateDisplayFormat, formattedVariableValue } from "../../utils"
import { DocumentsContext, NotificationContext, SingleDocumentContext, UserContext } from "../../context"
import ActionsDropdown from "../UI/ActionsDropdown"

const commentAuthorLabel = (comment, general = false) => {
  let authorName = ''
  if (comment.author && comment.author.firstname && comment.author.lastname) {
    authorName = `${comment.author.firstname} ${comment.author.lastname}`
  }
  let authorType = comment.author?.type === 'negotiator' ? 'Agent' : comment.author?.type === 'manager' ? 'Manager' : comment.author?.type === 'client' ? 'Client' : comment.author?.type === 'notary' ? 'Notaire' : comment.author?.type === 'lawyer' ? 'Avocat' : comment.author?.type === 'external' ? 'Externe' : 'Autre'
  let label = ''
  if(general) {
    label = `Commentaire général de ${authorName}, ${authorType}`
  } else {
    label = `${authorName}`
  }
  return label
}

const CommentsView = ({
  commentSections,
  comments,
  isGeneralComments,
  canAddComment,
  isSummaryView = false,
  modal = false,
  variableValue,
}) => {

  const { promptAddComment, promptResolveSuggestion, documentId } = useContext(SingleDocumentContext)
  const { updateDocumentRevision } = useContext(DocumentsContext)
  const { user, userClaims }  = useContext(UserContext)
  const { setNotification } = useContext(NotificationContext)
  const canManageSuggestions = isSummaryView ? false : (user?.validation_access === 'manager' || user?.validation_access === 'negotiator')
  const addCommentEnabled = canAddComment && (user?.validation_access || userClaims?.validation_access === 'comment' || userClaims?.validation_access === 'edit')

  const handleConfirmSuggestion = (suggestion) => {
    promptResolveSuggestion(suggestion, 'confirm')
  }

  const handleCancelSuggestion = (suggestion) => {
    promptResolveSuggestion(suggestion, 'cancel')
  }

  const handleDeleteComment = async (comment, isGeneralComments) => {
    try {
      const updates = {
        deleteComment: comment,
        isGeneralComment: isGeneralComments || false,
      }
      await updateDocumentRevision(documentId, updates)
      setNotification({ msg: "Commentaire supprimé", type: 'success' })
    } catch (error) {
      setNotification({ msg: "Erreur lors de la suppression du commentaire", type: 'danger' })
    }
  }

  const handleToggleCommentVisibility = async (comment, isGeneralComments) => {
    try {
      const updates = {
        toggleCommentVisibility: comment,
        isGeneralComment: isGeneralComments || false,
      }
      await updateDocumentRevision(documentId, updates)
      setNotification({ msg: "Visibilité du commentaire mise à jour", type: 'success' })
    } catch (error) {
      setNotification({ msg: "Erreur lors de la mise à jour de la visibilité du commentaire", type: 'danger' })
    }
  }

  return (
    <div className="comments flex flex-column gap-2">
      {comments &&
        <div className="comments__general flex flex-column gap-2">
          {comments.map((comment, index) => (
            <CommentBlock
              key={`comment-${index}`}
              comment={{
                ...comment,
                type: 'comment',
              }}
              handleCancelSuggestion={handleCancelSuggestion}
              handleConfirmSuggestion={handleConfirmSuggestion}
              canManageSuggestions={canManageSuggestions}
              isGeneralComments={isGeneralComments}
              onDelete={() => handleDeleteComment(comment, isGeneralComments)}
              onToggleVisibility={() => handleToggleCommentVisibility(comment, isGeneralComments)}
            />
          ))}
        </div>
      }
      {commentSections && commentSections.map((section, sectionIndex) => (
        <div key={`comment-section-${sectionIndex}`} className="comments__section gap-2">
          {section.title && <div className="flex flex-row justify-between my-2 comment-section__header">
            <h3 className={`comments__section-title ${modal ? 'comments__section-title-modal' : ''}`}>{section.title}</h3>
            {addCommentEnabled && (
              <button
                className="btn btn--small btn--white btn--border-radius-sm"
                onClick={() => {
                  promptAddComment({section: { ...section, index: sectionIndex, content: section.title }})
                }}
              >
                <AddComment fontSize="small" />
              </button>
            )}
          </div>}
          {variableValue && <div className="comments__variable-value">{variableValue}</div>}
          <div className='flex flex-column gap-2'>
            {section.comments.map((comment, commentIndex) => (
              <CommentBlock
                key={`comment-section-${sectionIndex}-block-${commentIndex}`}
                comment={comment}
                handleCancelSuggestion={handleCancelSuggestion}
                handleConfirmSuggestion={handleConfirmSuggestion}
                canManageSuggestions={canManageSuggestions}
                isGeneralComments={isGeneralComments}
                onDelete={() => handleDeleteComment(comment)}
                onToggleVisibility={() => handleToggleCommentVisibility(comment)}
                modal={modal}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

const CommentBlock = ({ comment, handleCancelSuggestion, handleConfirmSuggestion, canManageSuggestions, isGeneralComments, onDelete, onToggleVisibility, modal = false }) => {

  const { user } = useContext(UserContext)
  const { publicView } = useContext(SingleDocumentContext)

  const actionDropdownRef = React.createRef()

  const isMyComment = comment.author?.id && user?.id && (comment.author?.id).toString() === user.id?.toString()

  const handleDelete = () => {
    onDelete()
    if(actionDropdownRef.current) {
      actionDropdownRef.current.close()
    }
  }

  const handleToggleVisibility = () => {
    onToggleVisibility()
    if(actionDropdownRef.current) {
      actionDropdownRef.current.close()
    }
  }

  return (
    <div className={`comment__block ${modal ? 'comment__block-modal' : ''}`}>
      <div className='flex flex-row justify-between'>
        <div className="comment__block-author"><AddCommentOutlined fontSize="small" /> {commentAuthorLabel(comment, isGeneralComments)}</div>
        <div className="flex flex-row align-center justify-center gap-2">
          <span className={`todo-action-box__badge ${comment.type || 'comment'}`}>
            {comment.type === 'comment' && !publicView && (
              comment.shared_external ? <Visibility fontSize="small" className="mr-2" /> : <VisibilityOff fontSize="small" className="mr-2" />
            )}
            {comment.type === 'suggestion' ? 'Proposition' : 'Commentaires'}
          </span>
          {isMyComment && comment.type === 'comment' && (
            <ActionsDropdown
              align="right"
              trigger={
                <button 
                  className="icon-btn icon-btn--ghost icon-btn--svg-xl more-vert-icon">
                  <span className="svg-icon"><MoreVert fontSize='medium' /></span>
                </button>
              }
              hideHeader
              width={300}
              dropdownClass="actions-dropdown--no-padding"
              ref={actionDropdownRef}
            >
              <div className='dashboard-column__dropdown'>
                <ul>
                  <li onClick={handleToggleVisibility} className="!w-full edit"><span className="icon">{comment.shared_external ? <VisibilityOff /> : <Visibility />}</span>{comment.shared_external ? 'Rendre invisible pour les clients / notaires / avocats' : 'Rendre visible pour les clients / notaires / avocats'}</li>
                </ul>
                <ul>
                  <li onClick={handleDelete} className="!w-full trash"><span className="icon"><DeleteOutline /></span>Supprimer</li>
                </ul>
              </div>
            </ActionsDropdown>
          )}
        </div>
      </div>
      {comment.variable && <div className="comment__block-date">{comment.variable?.display_name || comment.variable?.tooltip || comment.variable?.variable || 'unknown variable'}</div>}
      <div className="comment__block-content"><Notes fontSize="small" /> {(isGeneralComments || comment.type !== 'suggestion') ? comment.content : comment.value ? comment.variable ? formattedVariableValue(comment.variable, comment.value) : comment.value : ''}</div>
      <div className="comment__block-date">{moment(comment.date).format(dateDisplayFormat)}</div>
      {comment.type === 'suggestion' && canManageSuggestions && (
        <div className='flex flex-row justify-end gap-2'>
          <button
            className="btn btn--small btn--danger  btn--with-icon btn--border-radius-sm"
            onClick={() => handleCancelSuggestion(comment)}
          >
            <Cancel fontSize="small" />
            Refuser
          </button>
          <button
            className="btn btn--small btn--success btn--with-icon btn--border-radius-sm"
            onClick={() => handleConfirmSuggestion(comment)}
          >
            <CheckCircle />
            Valider
          </button>
        </div>
      )}
    </div>
  )
}

export default CommentsView
